<script lang="ts" setup>
import { ref, watch, onMounted } from "vue";
import { usePGRESTAPIStore } from "../store/pgrest_api";
import PGRESTResult from "../classes/PGRESTResult";
import { ElMessage } from "element-plus";
import { Avatar, Bell, Edit, Delete, Document, Plus, RefreshLeft, CircleCheck, CircleClose, View, Watch } from "@element-plus/icons-vue";
import { storeToRefs } from "pinia";
import { useMainStore } from "../store/main";
import AddEdit_GreekStateGuarantee from "../components/AddEdit_GreekStateGuarantee.vue";
import UserType from "../enums/UserType";
import { useSort } from "../composables/sort";
import GreekStateGuarantees from "../classes/DB_Entities/GreekStateGuarantees";
import { useGenericMethodsVariables } from "../composables/genericMethodsVariables";
import ChangeApprovalStatus from "../enums/ChangeApprovalStatus";
const { showError } = useGenericMethodsVariables();

const pgrestapi = usePGRESTAPIStore();
const mainStore = useMainStore();
const DeleteDialogVisible = ref(false);
const { globalPortfolioID } = storeToRefs(mainStore);
const { globalDebtorID } = storeToRefs(mainStore);
const { globalLoanAgreementID } = storeToRefs(mainStore);
const { onSortChange, sortColumn, sortOrder } = useSort();

let DeleteSelectedRow = ref(0);
let filterPending = ref(false);
let dataList = ref([]);
let dataCount = ref(0);
let isLoading = ref(false);
let lastPage = ref(0);
let addEditVisible = ref(false);
let selectedRow = ref(0);
let filterDebtorFullname = ref<string>("");
let filterDebtorVAT = ref<string>("");
let filterProvision = ref<string>("");
let filterGuaranteedAmount = ref<string>("");
let filterPendingNew = ref(false);
let filterHideRejected = ref(true);
let deleteVisible = ref(false);
let timer = ref<any>(null);
let isNewEntity = ref(false);
let viewOnly = ref(false);

watch(globalPortfolioID, async () => {
  if (mainStore.loggedUser.username !== '') // Do not load if user logged out (changes the value of globalLoanAgreementID to "")
    await loadPage(1);
});

watch(globalDebtorID, async () => {
  if (mainStore.loggedUser.username !== '') // Do not load if user logged out (changes the value of globalLoanAgreementID to "")
    await loadPage(1);
});

watch(globalLoanAgreementID, async () => {
  if (mainStore.loggedUser.username !== '') // Do not load if user logged out (changes the value of globalLoanAgreementID to "")
    await loadPage(1);
});

const handleClickEdit = (row: any) => { //(row_id: number) => {
  viewOnly.value = !isEditable(row);
  selectedRow.value = row.id;
  isNewEntity.value = false;
  addEditVisible.value = true;
};

onMounted(async () => {
  loadPage(1);
});

const loadPage = async (page: number) => {
  isLoading.value = true;

  lastPage.value = page;
  page = page - 1;

  let filterUrl = "";

  if (filterPending.value) {
    filterUrl += "&pending_approvals=gt.0";
  }

  if (filterDebtorFullname.value !== "") {
    filterUrl += `&fullname=ilike.*${filterDebtorFullname.value}*`;
  }

  if (filterDebtorVAT.value !== "") {
    filterUrl += `&vat_number=ilike.*${filterDebtorVAT.value}*`;
  }

  if (filterProvision.value !== "") {
    filterUrl += `&kua_paroxhs_eed=ilike.*${filterProvision.value}*`;
  }

  if (filterGuaranteedAmount.value !== "") {
    filterUrl += `&poso_eggyhseos=eq.${filterGuaranteedAmount.value}`;
  }

  if (filterPendingNew.value) {
    filterUrl += "&approve_status=eq.0";
  }

  if (filterHideRejected.value) {
    filterUrl += "&approve_status=neq.2";
  }

  if (sortOrder.value !== "") {
    filterUrl += `&order=${sortColumn.value}.${sortOrder.value}`;
  }

  let selectedPortfolio = "";
  let selectedDebtor = "";
  let selectedLoanAgreement = "";
  let addedFilter = "";

  if (mainStore.globalPortfolioID !== "") {
    selectedPortfolio = mainStore.globalPortfolioID;
    addedFilter += "&portfolio=eq." + selectedPortfolio;
  }

  if (mainStore.globalDebtorID !== "") {
    selectedDebtor = mainStore.globalDebtorID;
    addedFilter += "&debtor_id=eq." + selectedDebtor;
  }

  if (mainStore.globalLoanAgreementID !== "") {
    selectedLoanAgreement = mainStore.globalLoanAgreementID;
    addedFilter += "&loan_agreement_id=eq." + selectedLoanAgreement;
  }

  if (mainStore.loggedUser.user_type == UserType.Agent) {
    addedFilter += `&allowed_users=cs.{${mainStore.loggedUser.id}}`;
  } else if (mainStore.loggedUser.user_type == UserType.Supervisor) {
    addedFilter += `&portfolio_allowed_users=cs.{${mainStore.loggedUser.id}}`;
  }

  let result: PGRESTResult = await pgrestapi.getPaginated(
    "view_mosgreekstateguarantees_view1?select=*" + addedFilter + filterUrl,
    page
  );

  if (result.error) {
    showError(result.error.message);

    return;
  }

  if (result.headers)
    dataCount.value = parseInt(result.headers["content-range"].split("/")[1]);

  dataList.value = result.data;

  isLoading.value = false;
};

const onFailSave = async () => {
  selectedRow.value = -1;
  ElMessage({
    showClose: true,
    message: "Greek State Guarantee failed to update",
    type: "error",
    duration: 2000,
  });

  addEditVisible.value = false;
};

const onAdd = async () => {
  ElMessage({
    showClose: true,
    message: "Greek State Guarantee added Successfully", // TODO: Change text for update
    type: "success",
    duration: 1000,
  });

  addEditVisible.value = false;

  loadPage(lastPage.value);
};

const filterChanged = async (value: string) => {
  if (timer.value !== 0) {
    clearTimeout(timer.value);
  }

  timer.value = setTimeout(() => {
    loadPage(1);

    timer.value = 0;
  }, 500);
};

const handleClickAdd = () => {
  addEditVisible.value = true;
  isNewEntity.value = true;
  selectedRow.value = -1;
};

const deleteHandleClick = (row_id: number) => {
  DeleteSelectedRow.value = row_id;
  deleteVisible.value = true;
  DeleteGRGuarantee(row_id);
};

const DeleteGRGuarantee = async (row_id: number) => {
  let result: PGRESTResult = new PGRESTResult();

  let id = JSON.stringify(row_id);

  result = await pgrestapi.delete_data("greek_state_guarantees?id=eq." + id);

  if (result.error) {
    showError(result.error.message);

    DeleteDialogVisible.value = false;
    return;
  }

  onDelete();
};

const onDelete = async () => {
  ElMessage({
    showClose: true,
    message: "Greek State Guarantee Deleted Successfully",
    type: "success",
    duration: 1000,
  });

  DeleteDialogVisible.value = false;
  loadPage(lastPage.value);
};

const localSortChange = async (sortObj: any) => {
  onSortChange(sortObj);
  await loadPage(lastPage.value);
};

const pendingFilterChange = async () => {
  await loadPage(1);
};

const onCloseAddEdit = () => {
  addEditVisible.value = false;
  selectedRow.value = -1;
};

const entityApprove = async (entity_id: number, accepted: boolean) => {
  console.log("entity id", entity_id, "approved", accepted);
  if (entity_id === undefined || entity_id === null) return;

  let result = await pgrestapi.patch(
    `${GreekStateGuarantees.getTableNameStatic()}?id=eq.${entity_id}`,
    {
      approve_status: accepted ? ChangeApprovalStatus.Approved : ChangeApprovalStatus.Rejected,
    }
  );

  ElMessage({
    showClose: true,
    message: `Guarantee${!accepted ? " not " : " "}approved`,
    type: "success",
    duration: 1000,
  });
  await loadPage(lastPage.value);
};

const isEditable = (row: any) => {
  return (row.debtor_approve_status === 1 && row.loan_agreement_approve_status === 1);
}
</script>

<template>
  <div class="content-wrapper" v-loading="isLoading">
    <el-row :gutter="10">
      <el-col :span="12">
        <h3 class="heading-page">
          {{ $t("GreekStateGuaranteesView.greek_state_guarantees") }} ({{ dataCount }})
        </h3>
      </el-col>
    </el-row>

    <div style="margin: 30px">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-input v-model="filterProvision" class="client-input" :placeholder="
            $t('GreekStateGuaranteesView.kya_of_greek_state_guarantee_provision')
          " @input="filterChanged" />
        </el-col>
        <el-col :span="6">
          <el-input v-model="filterDebtorFullname" class="client-input"
            :placeholder="$t('DebtorView.debtor_company_name')" @input="filterChanged" />
        </el-col>
        <el-col :span="6">
          <el-input v-model="filterDebtorVAT" class="client-input"
            :placeholder="$t('DebtorView.debor_tax_registration_nbr')" @input="filterChanged" />
        </el-col>
      </el-row>
    </div>

    <div>
      <el-row :gutter="10" style="margin: 10px; margin-top: 15px">
        <el-col :span="12" class="realestate-add-new-btn">
          <el-switch v-model="filterPending" size="small" :active-text="$t('global.pending_changes')" inactive-text=""
            @change="pendingFilterChange()" style="float: left" />
          <el-switch v-model="filterPendingNew" size="small" :active-text="$t('global.pending_changes_new_entities')"
            inactive-text="" @change="pendingFilterChange()" style="float: left" />
          <el-switch v-model="filterHideRejected" size="small" :active-text="$t('global.hide_rejected_entities')"
            inactive-text="" @change="pendingFilterChange()" style="float: left" />
        </el-col>
        <el-col :span="12" class="realestate-add-new-btn">
          <el-button link type="primary" size="small" class="btn-add" @click="loadPage(lastPage)"
            style="float: right; margin: 2.5px" :icon="RefreshLeft" />
          <el-button link type="primary" size="small" class="btn-add" style="float: right; margin: 2.5px"
            @click="handleClickAdd()" :icon="Plus" />
        </el-col>
      </el-row>
    </div>

    <el-table v-loading="isLoading" :data="dataList" stripe border table-layout="fixed" sortable="custom"
      @sort-change="localSortChange" size="small">
      <el-table-column sortable fixed prop="ar_sumbashs" :label="$t('GreekStateGuaranteesView.loan_agreement')" />
      <el-table-column sortable fixed prop="kua_paroxhs_eed"
        :label="$t('GreekStateGuaranteesView.kya_of_greek_state_guarantee_provision')" />
      <el-table-column fixed prop="fullname" :label="$t('DebtorView.debtor_company_name')" sortable />
      <el-table-column fixed prop="vat_number" :label="$t('DebtorView.debor_tax_registration_nbr')" sortable />
      <el-table-column sortable fixed prop="poso_eggyhseos" :label="$t('GreekStateGuaranteesView.guaranteed_amount')" />
      <el-table-column sortable fixed prop="pending_approvals" :label="$t('global.pending_approvals')" />
      <el-table-column fixed="right" :label="$t('global.operations')" width="280">
        <template #default="scope">
          <el-button link type="primary" size="small" class="btn-detail" @click="handleClickEdit(scope.row)"
            :icon="isEditable(scope.row) ? Edit : View" />
          <el-tooltip v-if="scope.row.approve_status == 0 && mainStore.loggedUser.user_type === UserType.Agent"
            effect="dark" content="Pending Supervisor approval" placement="top">
            <el-button size="small" type="primary" class="btn-warn" :icon="Watch" circle />
          </el-tooltip>
          <el-button v-if="mainStore.loggedUser.user_type === UserType.Administrator" link type="primary" size="small"
            class="btn-delete" @click="DeleteDialogVisible = true; DeleteSelectedRow = scope.row.id;" :icon="Delete" />
          <span v-if="
            mainStore.loggedUser.user_type < UserType.Agent &&
            scope.row.approve_status === 0
          ">
            <el-button class="btn-accept_square" @click="entityApprove(scope.row.id, true)" :icon="CircleCheck" />
            <el-button class="btn-reject_square" @click="entityApprove(scope.row.id, false)" :icon="CircleClose" />
          </span>
          <el-popover v-if="scope.row.pending_approvals > 0" placement="top-start" title="" :width="155" trigger="hover"
            :content="$t('global.pending_changes')">
            <template #reference>
              <el-button :icon="Bell" circle size="small" style="background-color: coral; border: 0px; color: white" />
            </template>
          </el-popover>
          <el-popover v-if="scope.row.debtor_approve_status !== 1" placement="top-start" title="" :width="205"
            trigger="hover" :content="$t('global.debtor_not_approved')">
            <template #reference>
              <el-button :icon="Avatar" circle size="small"
                style="background-color: blueviolet; border: 0px; color: white" />
            </template>
          </el-popover>
          <el-popover v-if="scope.row.loan_agreement_approve_status !== 1" placement="top-start" title="" :width="265"
            trigger="hover" :content="$t('global.loan_agreement_not_approved')">
            <template #reference>
              <el-button :icon="Document" circle size="small"
                style="background-color: coral; border: 0px; color: white" />
            </template>
          </el-popover>

        </template>
      </el-table-column>
    </el-table>

    <div>
      <el-pagination size="small" layout="prev, pager, next" :page-size="10" :total="dataCount" @current-change="loadPage" />
    </div>

    <el-dialog v-if="addEditVisible" v-model="addEditVisible" @update:model-value="onCloseAddEdit"
      :title="isNewEntity ? 'Add Greek State Guarantee' : 'Greek State Guarantee Details'">
      <AddEdit_GreekStateGuarantee :greek_state_guarantee_id="selectedRow" :new-entity="isNewEntity"
        @close="onCloseAddEdit" @save="onAdd" @fail-save="onFailSave" :view_only="viewOnly" />
    </el-dialog>

    <el-dialog v-model="DeleteDialogVisible" title="Greek State Guarantee Delete" width="50%">
      <span>Are you sure you want to delete this Property?</span>
      <template #footer #default="scope">
        <span class="dialog-footer">
          <el-button @click="DeleteDialogVisible = false">{{ $t('generic.cancel') }}</el-button>
          <el-button type="primary" @click="deleteHandleClick(DeleteSelectedRow)">{{ $t('generic.delete') }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped></style>
